/**
 * The onboarding chat's deck and website parse state: the in-flight parse
 * controllers and count, the Build CTA parse gate, and the per-batch nudge
 * bookkeeping.
 *
 * Invariants:
 * - Every write after a parse's await MUST check that its captured
 *   materialsEpochRef value still matches (see the ref below).
 * - resetMaterialsParse aborts both controllers before it clears anything.
 * - Unmount aborts both controllers, so a late "ready" can't act for a
 *   founder who already left.
 * - Abort listeners on these controllers MUST only schedule async work: the
 *   abort event fires synchronously, during reset and before the component's
 *   own unmount cleanup.
 */
"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { DeckParseProgressCounts } from "@/lib/onboarding/deckParseProgress";

/** Which materials slot a request targets; the status GET reports each one. */
export type MaterialsSlot = "deck" | "site";

export function useMaterialsParseState() {
  // In-flight materials parses: counts active background polls (deck + site).
  // The build confirm effect suppresses the CTA while this is > 0 and the
  // founder has not hit the message limit, so the model can deliver the
  // materials critique before the handoff.
  const [inFlightParses, setInFlightParses] = useState(0);
  const inFlightParsesCountRef = useRef(0);
  // Whether a streamed "complete" was suppressed by the materials parse gate.
  // Ref for synchronous reads in effects/callbacks; state mirror for JSX.
  const suppressedCompleteRef = useRef(false);
  const [isMaterialsSuppressed, setIsMaterialsSuppressed] = useState(false);
  // Tracks whether ANY in-flight parse finished "ready" (vs all "failed"),
  // so the nudge fires when the last parse lands if at least one succeeded.
  const anyParseReadyRef = useRef(false);
  // Tracks whether any ready parse in the batch still owes the founder a read,
  // per the server's readDue (the client's streamed deckParsed/siteRead flags
  // are not trustworthy — stripUnearnedReadFlags in persistTurn.ts). Reset to
  // false per batch like anyParseReadyRef; a missing readDue field (older
  // response shape) sets it true so the fallback stays today's nudge. Only
  // stays false when every ready source's readDue confirmed the read already
  // landed, which means settle should release the gate, not ask Lev to read
  // the same deck twice.
  const anyReadStillDueRef = useRef(false);
  // Sources that landed "ready" in the current in-flight batch (accumulated
  // per settle, read out and cleared alongside anyParseReadyRef/anyReadStillDueRef).
  const pendingNudgeSourcesRef = useRef<Set<MaterialsSlot>>(new Set());
  // Frozen at settle time: the exact sources a currently-held/pending nudge is
  // for. resolveMaterialsGate consumes this once, at fire time.
  const nudgeTargetSourcesRef = useRef<Set<MaterialsSlot>>(new Set());
  // The x-lev-read-due header of the most recently COMPLETED chat turn (null
  // before any turn finishes). IdeaChatInput's readDueFetch writes it when that
  // turn's response is still the active generation — see the staleness guard
  // there. This is the only trustworthy "was the read already delivered"
  // signal: a materials-poll GET during the turn can see the pre-persist
  // session and report the same source still due (LEV-1969).
  const lastTurnReadDueRef = useRef<Set<MaterialsSlot> | null>(null);
  // Bumped by Reset. A parse from the discarded conversation must never
  // nudge, toast, capture, report analytics or touch the new conversation's
  // in-flight count. Reset aborts the latest deck and site controllers, but
  // an older poll (a second deck or site replaced the ref) keeps running, so
  // every write after a parse's await MUST check that its captured epoch
  // still matches.
  const materialsEpochRef = useRef(0);
  // True from a "suppressed" outcome until its terminal outcome (nudged or
  // released) is sent; reported as wasSuppressed. Every path that clears
  // suppressedCompleteRef MUST send that terminal outcome, or the analytics
  // gate stays open forever. The one exception is resetMaterialsParse: Reset
  // discards the conversation the gate belonged to, so it clears both flags
  // without an event.
  const gateWasSuppressedRef = useRef(false);
  // Server-reported deck parse progress while the deck poll runs; null
  // renders the answers progress bar instead.
  const [deckProgress, setDeckProgress] = useState<DeckParseProgressCounts | null>(null);

  // Abort controller for the in-flight deck vision parse (POST + up-to-5-minute
  // poll). Aborted on unmount so a late "ready" can't run the post-upload
  // continuation (finishUpload / handleSubmit / navigation) or setState for a
  // founder who already navigated away (LEV-1293, PR #518 review).
  const visionParseAbortRef = useRef<AbortController | null>(null);
  // Same contract for the in-flight website parse (POST + poll) — its own
  // controller because a deck and a site can be in flight at the same time.
  const siteParseAbortRef = useRef<AbortController | null>(null);
  // Hosts already staged for a website parse this conversation (LEV-1431).
  // Best-effort client dedupe so a founder who mentions their site twice doesn't
  // POST twice; the server's per-slot idempotency is the authority. A ref, not
  // state: nothing renders from it.
  const parsedSiteHostsRef = useRef<Set<string>>(new Set());
  // Settles once a program deck's parse POST has settled. A turn sent to the
  // model before then waits on it, so the chat route reads the deck's
  // processing marker instead of answering with no deck on file.
  const deckStagingRef = useRef<Promise<void> | null>(null);

  // Set when a parse lands ready while a turn is busy. The nudge is decided at
  // the next turn end, AFTER any queued founder answer has gone out (that
  // answer's own turn end decides it). Suppression stays on until the next
  // send, so the CTA can't pop first. A flag, never founder text. Any sent
  // turn (handleSubmit), Reset, Skip and the Build CTA clear it.
  const nudgeHeldRef = useRef(false);

  // Mirrors the chat route's one-ask DECK FAST PATH (programContext.ts): the
  // sources (a dropped deck, a pasted website) staged in a shortened program
  // chat, each removed again if its parse fails, as the route's fast path
  // drops a failed source. Only while it is non-empty may Lev end a turn
  // without a question, announcing a read still running.
  const materialsFastPathRef = useRef<Set<MaterialsSlot>>(new Set());
  // Fast-path reads that failed with no turn reaching the server since.
  // Lev's announcement then owes the founder a question; settled by
  // tellReadUnreadable once no turn is busy.
  const readLostRef = useRef<Set<MaterialsSlot>>(new Set());

  // Reads the refs at call time, so the unmount cleanup aborts whichever
  // controllers are live then, not the ones that existed at mount.
  const abortParses = useCallback(() => {
    visionParseAbortRef.current?.abort();
    siteParseAbortRef.current?.abort();
  }, []);
  useEffect(() => abortParses, [abortParses]);

  const resetMaterialsParse = useCallback(() => {
    // Parse polls read the slot of whatever session the cookie names — after
    // the detach that is the next conversation's, so a live poll would
    // report the discarded deck/site into it.
    abortParses();
    // Cleared in place, unlike the sets below, which are replaced.
    parsedSiteHostsRef.current.clear();
    suppressedCompleteRef.current = false;
    setIsMaterialsSuppressed(false);
    nudgeHeldRef.current = false;
    gateWasSuppressedRef.current = false;
    materialsFastPathRef.current = new Set();
    readLostRef.current = new Set();
    deckStagingRef.current = null;
    materialsEpochRef.current += 1;
    setDeckProgress(null);
    anyParseReadyRef.current = false;
    anyReadStillDueRef.current = false;
    pendingNudgeSourcesRef.current = new Set();
    nudgeTargetSourcesRef.current = new Set();
    lastTurnReadDueRef.current = null;
    inFlightParsesCountRef.current = 0;
    setInFlightParses(0);
  }, [abortParses]);

  return {
    inFlightParses,
    setInFlightParses,
    inFlightParsesCountRef,
    suppressedCompleteRef,
    isMaterialsSuppressed,
    setIsMaterialsSuppressed,
    anyParseReadyRef,
    anyReadStillDueRef,
    pendingNudgeSourcesRef,
    nudgeTargetSourcesRef,
    lastTurnReadDueRef,
    materialsEpochRef,
    gateWasSuppressedRef,
    deckProgress,
    setDeckProgress,
    visionParseAbortRef,
    siteParseAbortRef,
    parsedSiteHostsRef,
    deckStagingRef,
    nudgeHeldRef,
    materialsFastPathRef,
    readLostRef,
    resetMaterialsParse,
  };
}
